// src/components/ProductCard.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Heart, ShoppingCart } from 'lucide-react';
import { Product } from '../types';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import VanillaTilt from "vanilla-tilt";

// Update Backend base URL
const backendBaseURL = import.meta.env.VITE_API_BASE_URL;
const backendBaseImagesURL = import.meta.env.VITE_API_BASE_IMAGES_URL;

// Helper function to get full image URL
const getFullImageURL = (url: string): string => {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `${backendBaseImagesURL}${url}`;
};

interface ProductCardProps {
  product: Product;
  variant?: 'default' | 'featured';
}

export const ProductCard: React.FC<ProductCardProps> = ({ product, variant = 'default' }) => {
  const { state, dispatch } = useApp();
  const navigate = useNavigate();
  const isInWishlist = state.wishlist.some(item => item.id === product.id);
  const [fullProduct, setFullProduct] = useState<Product | null>(null);

  // State for showing the variation selection modal
  const [showVariationModal, setShowVariationModal] = useState(false);

  // Ref for tilt effect (only in featured variant)
  const cardRef = useRef<HTMLDivElement>(null);

  // Determine the primary image
  const primaryImage =
    product.images && product.images.length > 0
      ? getFullImageURL(product.images[0].url)
      : '/path/to/default-image.jpg'; // Replace with your actual default image path

  // Helper function to truncate text to 100 characters max.
  const truncateText = (text: string, maxLength: number = 100): string => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength).trim() + '...' : text;
  };

  // When the card (or its buttons) is clicked...
  const handleClick = async () => {
    try {
      // Use the slug for navigation instead of ID
      navigate(`/product/${product.slug}`);
    } catch (err) {
      console.error('Failed to navigate to product:', err);
      toast.error('Could not load product details');
    }
  };

  // Handle Add to Cart on the card
  const handleAddToCart = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!fullProduct) {
      try {
        const response = await fetch(`${backendBaseURL}/products/${product.id}`);
        const data = await response.json();
        setFullProduct(data);
        
        if (data.variations?.type && data.variations.type !== 'none') {
          setShowVariationModal(true);
          return;
        }
      } catch (err) {
        toast.error('Could not load product details');
        return;
      }
    }

    if (fullProduct?.variations?.type && fullProduct.variations.type !== 'none') {
      setShowVariationModal(true);
    } else if (fullProduct) {
      dispatch({
        type: 'ADD_TO_CART',
        payload: { 
          ...fullProduct,
          id: fullProduct.id,
          quantity: 1,
          price: Number(fullProduct.price),
          selectedVariations: {} 
        },
      });
      toast.success('Added to cart');
    }
  };

  const handleToggleWishlist = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the card's onClick
    dispatch({ type: 'TOGGLE_WISHLIST', payload: product });
  };

  // ---------------- Variation Modal Component ----------------
  interface VariationModalProps {
    variations: {
      type: string;
      colors: string[];
      sizes: string[];
      ageRanges: string[];
      stockByVariation: any[];
    };
    product: Product;
    onConfirm: (selectedVariations: { color?: string; size?: string; ageRange?: string }) => void;
    onClose: () => void;
  }

  const VariationModal: React.FC<VariationModalProps> = ({ variations, product, onConfirm, onClose }) => {
    const [localSelection, setLocalSelection] = useState<{
      color?: string;
      size?: string;
      ageRange?: string;
    }>({});

    // Compute available Age Ranges
    const availableAgeRanges = React.useMemo(() => {
      if (variations.type === 'color-age' && localSelection.color) {
        const colorNode = variations.stockByVariation.find(
          (v: any) => v.color === localSelection.color && v.parentId === null
        );

        if (colorNode?.id) {
          const childAges = variations.stockByVariation
            .filter((child: any) => {
              return child.parentId === colorNode.id && child.ageRange;
            })
            .map((child: any) => child.ageRange);
          
          return Array.from(new Set(childAges));
        }
      }
      return variations.ageRanges;
    }, [variations, localSelection.color]);

    // Compute available Sizes
    const availableSizes = React.useMemo(() => {
      if (variations.type.includes('size') && localSelection.color && !localSelection.ageRange) {
        const colorNode = variations.stockByVariation.find(
          (v: any) => v.color === localSelection.color && v.parentId === null
        );

        if (colorNode?.id) {
          const childSizes = variations.stockByVariation
            .filter((child: any) => {
              return child.parentId === colorNode.id && child.size;
            })
            .map((child: any) => child.size);
          
          return Array.from(new Set(childSizes));
        }
      }
      return variations.sizes;
    }, [variations, localSelection.color, localSelection.ageRange]);

    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div className="bg-white p-6 rounded-lg w-80">
          <h2 className="text-xl font-bold mb-4">Select Options for {product.name}</h2>

          {variations.colors && variations.colors.length > 0 && (
            <div className="mb-4">
              <h3 className="font-medium">Color</h3>
              <div className="flex flex-wrap gap-2 mt-2">
                {variations.colors.map((color: string) => (
                  <button
                    key={color}
                    onClick={() => {
                      setLocalSelection(prev => ({ ...prev, color }));
                    }}
                    className={`px-4 py-2 rounded border ${
                      localSelection.color === color
                        ? 'border-pink-500 bg-pink-50 text-pink-500'
                        : 'border-gray-300 hover:border-pink-500'
                    }`}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </div>
          )}

          {variations.sizes && variations.sizes.length > 0 && (
            <div className="mb-4">
              <h3 className="font-medium">Size</h3>
              <div className="flex flex-wrap gap-2 mt-2">
                {availableSizes.map((size: string) => (
                  <button
                    key={size}
                    onClick={() => {
                      setLocalSelection(prev => ({ ...prev, size }));
                    }}
                    className={`px-4 py-2 rounded border ${
                      localSelection.size === size
                        ? 'border-pink-500 bg-pink-50 text-pink-500'
                        : 'border-gray-300 hover:border-pink-500'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}

          {variations.ageRanges && variations.ageRanges.length > 0 && (
            <div className="mb-4">
              <h3 className="font-medium">Age Range</h3>
              <div className="flex flex-wrap gap-2 mt-2">
                {availableAgeRanges.map((age: string) => (
                  <button
                    key={age}
                    onClick={() => {
                      setLocalSelection(prev => ({ ...prev, ageRange: age }));
                    }}
                    className={`px-4 py-2 rounded border ${
                      localSelection.ageRange === age
                        ? 'border-pink-500 bg-pink-50 text-pink-500'
                        : 'border-gray-300 hover:border-pink-500'
                    }`}
                  >
                    {age}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-4">
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (variations.colors && variations.colors.length > 0 && !localSelection.color) {
                  toast.error('Please select a color');
                  return;
                }
                if (variations.sizes && variations.sizes.length > 0 && !localSelection.size) {
                  toast.error('Please select a size');
                  return;
                }
                if (variations.ageRanges && variations.ageRanges.length > 0 && !localSelection.ageRange) {
                  toast.error('Please select an age range');
                  return;
                }
                
                onConfirm(localSelection);
              }}
              className="px-4 py-2 bg-pink-500 text-white rounded hover:bg-pink-600"
            >
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    );
  };
  // ---------------- End of Variation Modal ----------------

  // Initialize VanillaTilt on the featured card for the tilt effect.
  useEffect(() => {
    if (cardRef.current && variant === 'featured') {
      VanillaTilt.init(cardRef.current, {
        max: 5,
        speed: 400,
        scale: 1.02,
      });
    }
    return () => {
      if (cardRef.current && (cardRef.current as any).vanillaTilt) {
        (cardRef.current as any).vanillaTilt.destroy();
      }
    };
  }, [variant]);

  // Render the card (featured or default)
  if (variant === 'featured') {
    return (
      <>
        <div
          ref={cardRef}
          onClick={handleClick}
          className="bg-white rounded-lg shadow-md overflow-hidden transition-shadow transform hover:-translate-y-1 cursor-pointer animate-fadeInUp"
        >
          <div className="relative">
            <img
              src={primaryImage}
              alt={product.name}
              className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-100 transition-opacity animate-custom-pulse">
              <div className="absolute bottom-4 left-4 right-4">
                <h3 className="text-xl font-semibold text-white mb-2">
                  {product.name}
                </h3>
                <p className="text-white/80 text-sm mb-4">
                  {truncateText(product.description)}
                </p>
                <div className="flex items-center justify-between">
                  <p className="text-white font-bold text-lg">
                    KES {product.price.toLocaleString()}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={handleToggleWishlist}
                      className={`p-2 rounded-full ${
                        isInWishlist ? 'bg-pink-500' : 'bg-white'
                      } hover:bg-pink-600 transition-colors`}
                    >
                      <Heart
                        className={`h-5 w-5 ${
                          isInWishlist ? 'text-white' : 'text-pink-500'
                        }`}
                      />
                    </button>
                    <button
                      onClick={handleAddToCart}
                      className="p-2 bg-pink-500 rounded-full hover:bg-pink-600 transition-colors"
                    >
                      <ShoppingCart className="h-5 w-5 text-white" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {showVariationModal && fullProduct?.variations && (
          <VariationModal
            variations={fullProduct.variations}
            product={product}
            onConfirm={(selected) => {
              dispatch({
                type: 'ADD_TO_CART',
                payload: { 
                  ...fullProduct!,
                  id: fullProduct!.id,
                  quantity: 1,
                  price: Number(fullProduct!.price),
                  selectedVariations: selected 
                },
              });
              setShowVariationModal(false);
              toast.success('Added to cart');
            }}
            onClose={() => setShowVariationModal(false)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div
        onClick={handleClick}
        className="bg-white rounded-lg shadow-md overflow-hidden group hover:shadow-xl transition-shadow cursor-pointer"
      >
        <div className="relative">
          <img
            src={primaryImage}
            alt={product.name}
            className="w-full h-48 object-cover group-hover:scale-105 transition-transform"
          />
          <div className="absolute top-2 right-2 space-y-2">
            <button
              onClick={handleToggleWishlist}
              className={`p-2 rounded-full shadow-md ${
                isInWishlist ? 'bg-pink-500' : 'bg-white'
              } hover:bg-pink-600 transition-colors`}
            >
              <Heart
                className={`h-5 w-5 ${
                  isInWishlist ? 'text-white' : 'text-pink-500'
                }`}
              />
            </button>
            <button
              onClick={handleAddToCart}
              className="p-2 bg-white rounded-full shadow-md hover:bg-pink-50"
            >
              <ShoppingCart className="h-5 w-5 text-pink-500" />
            </button>
          </div>
        </div>
        <div className="p-4">
          <h3 className="text-lg text-gray-800 truncate">
            {product.name}
          </h3>
          <p className="text-gray-600 text-sm mb-2">
            {product.category_name || `Category ID: ${product.category_id}`}
          </p>
          <p className="text-pink-500 font-bold">
            KES {product.price.toLocaleString()}
          </p>
        </div>
      </div>
      {showVariationModal && fullProduct?.variations && (
        <VariationModal
          variations={fullProduct.variations}
          product={product}
          onConfirm={(selected) => {
            dispatch({
              type: 'ADD_TO_CART',
              payload: { 
                ...fullProduct!,
                id: fullProduct!.id,
                quantity: 1,
                price: Number(fullProduct!.price),
                selectedVariations: selected 
              },
            });
            setShowVariationModal(false);
            toast.success('Added to cart');
          }}
          onClose={() => setShowVariationModal(false)}
        />
      )}
    </>
  );
};

export default ProductCard;
